import { useState, useEffect } from 'react';
import { ViewState, IngestionJob, TransactionResult, WalletState } from './types';
import IngestionEngine from './components/IngestionEngine';
import HealerInterface from './components/HealerInterface';
import VaultInterface from './components/VaultInterface';
import { Fingerprint, Wallet, Coins, ArrowRight, LogIn, Shield, LayoutGrid, Globe2, Languages, Scale, Clock, LogOut, Settings } from 'lucide-react';
import { wallet as walletApi } from './lib/api';
import { AuthProvider, useAuth } from './lib/authContext';
import AuthForm from './components/AuthForm';
import AdminPanel from './components/AdminPanel';
import SetupAdmin from './components/SetupAdmin';

// --- MAIN LAYOUT COMPONENT ---
const AppContent = () => {
  const { user, loading, signOut } = useAuth();
  const [view, setView] = useState<ViewState>(() => {
    // Check URL for admin route
    if (typeof window !== 'undefined' && window.location.pathname === '/admin') {
      return ViewState.ADMIN;
    }
    return ViewState.INGESTION;
  });
  const [showAuth, setShowAuth] = useState(false);
  const [currentJob, setCurrentJob] = useState<IngestionJob | null>(null);
  const [wallet, setWallet] = useState<WalletState>({ balance: 0, tokens: 0, payoutThreshold: 1000 });
  const [lastTransaction, setLastTransaction] = useState<TransactionResult | null>(null);

  // Handle URL changes for admin
  useEffect(() => {
    const handlePopState = () => {
      if (window.location.pathname === '/admin') {
        setView(ViewState.ADMIN);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Fetch wallet when user logs in
  useEffect(() => {
    if (user?.id) {
      walletApi.get(user.id)
        .then((data: any) => {
          setWallet({
            balance: (data.balance_cents || 0) / 100,
            tokens: data.tokens || 0,
            payoutThreshold: data.payout_threshold || 1000
          });
        })
        .catch((err) => {
          console.log('Wallet fetch skipped:', err.message);
          // Use defaults if wallet fetch fails
          setWallet({ balance: 0, tokens: 0, payoutThreshold: 1000 });
        });
    }
  }, [user?.id]);

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/';
  };

  const handleAuthSuccess = (userData: any) => {
    setShowAuth(false);
    // User will be automatically set by the auth context
  };

  const handleIngestionComplete = (job: IngestionJob) => {
    setCurrentJob(job);
    setTimeout(() => setView(ViewState.HEALER), 1500);
  };

  const handleTransaction = (result: TransactionResult) => {
    setLastTransaction(result);
    if (user?.id) {
      walletApi.get(user.id).then(setWallet).catch(console.error);
    }
    if (result.payoutTriggered) {
        setTimeout(() => setLastTransaction(null), 8000);
    } else {
        setTimeout(() => setLastTransaction(null), 3000);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#09090b] flex items-center justify-center text-zinc-500">Loading Izwi...</div>;

  // --- AUTH MODAL ---
  if (showAuth && !user) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/30 via-[#09090b] to-[#09090b]"></div>
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-md">
          {/* Back Button */}
          <button
            onClick={() => setShowAuth(false)}
            className="mb-6 flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            <span>Back to Landing</span>
          </button>
          
          <AuthForm onSuccess={handleAuthSuccess} />
        </div>
      </div>
    );
  }

  // --- LOGIN SCREEN ---
  if (!user) {
    return (
      <div className="min-h-screen bg-[#09090b] flex flex-col items-center justify-center p-6 relative overflow-hidden">
         {/* Animated Background */}
         <div className="absolute inset-0">
           <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/30 via-[#09090b] to-[#09090b]"></div>
           <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
           <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
         </div>
         
         <div className="relative z-10 max-w-4xl w-full text-center space-y-12">
            {/* Logo Section */}
            <div className="flex flex-col items-center space-y-6">
                <div className="relative group">
                  <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity animate-pulse"></div>
                  <div className="relative w-32 h-32 rounded-3xl flex items-center justify-center shadow-2xl shadow-purple-900/50 group-hover:scale-105 transition-transform duration-300 overflow-hidden">
                      <img src="/logo.png" alt="IZWI Logo" className="w-full h-full object-contain" />
                  </div>
                </div>
                
                <div className="space-y-4">
                    <h1 className="text-7xl md:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-200 to-blue-200 tracking-tight drop-shadow-2xl">
                        IZWI<span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">.ai</span>
                    </h1>
                    <p className="text-2xl text-zinc-300 font-light tracking-wide">
                        The African Multimodal Data Refinery
                    </p>
                    <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span>Powered by Cloudflare AI • Secured on the Edge • Paid via Polar</span>
                    </div>
                </div>
            </div>
                
            {/* Value Proposition Cards */}
            <div className="grid md:grid-cols-2 gap-8">
                {/* Earnings Card */}
                <div className="group relative bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border border-yellow-500/20 rounded-3xl p-8 hover:border-yellow-500/40 transition-all duration-300 hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-orange-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-yellow-500/20 rounded-2xl">
                                <Coins className="w-8 h-8 text-yellow-400" />
                            </div>
                            <h3 className="text-2xl font-bold text-white">Earn Real Money</h3>
                        </div>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-400">Top Healers Earn</span>
                                <span className="text-3xl font-bold text-yellow-400">R50/hour</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-400">Payout Threshold</span>
                                <span className="text-lg font-semibold text-white">1000 tokens</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-400">Payment Method</span>
                                <span className="text-lg font-semibold text-purple-400">Polar.sh</span>
                            </div>
                        </div>
                        <div className="pt-4 border-t border-yellow-500/20">
                            <p className="text-sm text-zinc-300">Quality-based rewards. Higher accuracy = higher payouts.</p>
                        </div>
                    </div>
                </div>

                {/* Impact Card */}
                <div className="group relative bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/20 rounded-3xl p-8 hover:border-purple-500/40 transition-all duration-300 hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-blue-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-purple-500/20 rounded-2xl">
                                <Languages className="w-8 h-8 text-purple-400" />
                            </div>
                            <h3 className="text-2xl font-bold text-white">Preserve Culture</h3>
                        </div>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-400">Languages Supported</span>
                                <span className="text-3xl font-bold text-purple-400">100+</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-400">Countries Covered</span>
                                <span className="text-lg font-semibold text-white">15+</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-400">Dialects Mapped</span>
                                <span className="text-lg font-semibold text-blue-400">500+</span>
                            </div>
                        </div>
                        <div className="pt-4 border-t border-purple-500/20">
                            <p className="text-sm text-zinc-300">Help build the world's largest African language dataset.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className="bg-gradient-to-r from-zinc-900/50 to-zinc-800/50 border border-zinc-700/50 rounded-3xl p-8 backdrop-blur-sm">
                <div className="grid grid-cols-3 gap-8">
                    <div className="text-center group">
                        <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400 group-hover:scale-110 transition-transform">R2.5M+</div>
                        <div className="text-sm text-zinc-500 mt-1">Paid to Healers</div>
                    </div>
                    <div className="text-center group">
                        <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 group-hover:scale-110 transition-transform">50K+</div>
                        <div className="text-sm text-zinc-500 mt-1">Hours Annotated</div>
                    </div>
                    <div className="text-center group">
                        <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 group-hover:scale-110 transition-transform">98%</div>
                        <div className="text-sm text-zinc-500 mt-1">Accuracy Rate</div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="space-y-6">
                <button 
                    onClick={() => setShowAuth(true)}
                    className="group relative w-full max-w-md mx-auto py-6 px-8 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 text-white font-bold text-xl rounded-2xl transition-all duration-500 flex items-center justify-center gap-4 shadow-2xl shadow-purple-900/50 hover:shadow-purple-900/70 hover:scale-105 transform"
                >
                    <LogIn className="w-6 h-6 group-hover:rotate-12 transition-transform" /> 
                    <span>Start Earning as a Data Healer</span>
                </button>
                
                <div className="flex items-center justify-center gap-6 text-sm text-zinc-500">
                    <div className="flex items-center gap-2 hover:text-zinc-300 transition-colors">
                        <Shield className="w-4 h-4" />
                        <span>Secure Auth</span>
                    </div>
                    <div className="w-px h-4 bg-zinc-700"></div>
                    <div className="flex items-center gap-2 hover:text-zinc-300 transition-colors">
                        <Coins className="w-4 h-4" />
                        <span>Instant Payouts</span>
                    </div>
                    <div className="w-px h-4 bg-zinc-700"></div>
                    <div className="flex items-center gap-2 hover:text-zinc-300 transition-colors">
                        <Clock className="w-4 h-4" />
                        <span>Flexible Hours</span>
                    </div>
                </div>
                
                <p className="text-zinc-600 text-sm font-mono animate-pulse">
                    Join 5,000+ African language experts • Earn while preserving culture
                </p>
            </div>
         </div>
      </div>
    );
  }

  // --- MAIN APP ---
  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex flex-col relative overflow-hidden">
      
      {/* Payout Notification */}
      {lastTransaction?.payoutTriggered && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-[#1a1a1a] border border-yellow-500/30 rounded-2xl p-8 max-w-md w-full shadow-[0_0_50px_-10px_rgba(234,179,8,0.3)] text-center relative overflow-hidden animate-in zoom-in-95 duration-300">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600"></div>
                <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-yellow-500/20">
                    <Wallet className="w-10 h-10 text-yellow-500" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">PAYOUT INITIATED</h2>
                <p className="text-zinc-400 mb-6 font-mono text-sm">Polar Ref: {lastTransaction.payoutReference}</p>
                
                <div className="bg-black/40 rounded-xl p-4 border border-zinc-800 mb-6">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-zinc-500 text-xs font-bold uppercase">Amount</span>
                        <span className="text-yellow-400 font-mono text-xl font-bold">R50.00 ZAR</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-zinc-500 text-xs font-bold uppercase">Method</span>
                        <span className="text-zinc-300 font-mono text-sm">MTN MoMo</span>
                    </div>
                </div>
                <button 
                    onClick={() => setLastTransaction(null)}
                    className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors"
                >
                    ACKNOWLEDGE RECEIPT
                </button>
            </div>
        </div>
      )}

      {/* Navbar */}
      <nav className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setView(ViewState.INGESTION)}>
            <div className="w-9 h-9 rounded-lg shadow-lg shadow-purple-900/50 overflow-hidden">
              <img src="/logo.png" alt="IZWI Logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold tracking-tight">IZWI<span className="text-purple-500">.ai</span></span>
          </div>
          
          <div className="hidden md:flex items-center gap-1 bg-zinc-900/50 p-1 rounded-lg border border-zinc-800">
            <NavButton label="Ingest" active={view === ViewState.INGESTION} onClick={() => setView(ViewState.INGESTION)} />
            <NavButton label="Heal" active={view === ViewState.HEALER} onClick={() => setView(ViewState.HEALER)} />
            <NavButton label="Vault" active={view === ViewState.VAULT} onClick={() => setView(ViewState.VAULT)} />
          </div>

          <div className="flex items-center gap-4">
             <div className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-full pl-3 pr-4 py-1.5">
                <div className="flex flex-col items-end leading-none">
                    <span className="text-[10px] text-zinc-500 font-bold uppercase">{user.clan}</span>
                    <span className="text-sm font-mono text-zinc-200">R{wallet.balance.toFixed(2)}</span>
                </div>
                <div className="h-6 w-px bg-zinc-800 mx-1"></div>
                 <div className="flex flex-col items-end leading-none">
                    <span className="text-[10px] text-zinc-500 font-bold uppercase">Verified</span>
                     <div className="flex items-center gap-1">
                        <span className={`text-sm font-mono font-bold ${wallet.tokens > 900 ? "text-yellow-500" : "text-purple-400"}`}>
                            {wallet.tokens}
                        </span>
                        <Coins className="w-3 h-3 text-zinc-500" />
                     </div>
                </div>
             </div>
             
             {/* User Menu */}
             <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-white">{user.displayName}</p>
                  <p className="text-xs text-zinc-500">{user.rank}</p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
                  title="Sign out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
             </div>
          </div>
        </div>
      </nav>

      <main className="flex-1 p-6 md:p-12 relative">
        {lastTransaction && !lastTransaction.payoutTriggered && (
            <div className="absolute top-6 right-12 text-green-400 font-bold animate-out fade-out slide-out-to-top-10 duration-1000 z-50 pointer-events-none">
                +{lastTransaction.tokensEarned} Tokens
            </div>
        )}

        {view === ViewState.INGESTION && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
             <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-zinc-500 bg-clip-text text-transparent">
                Multimodal Data Refinery
              </h1>
              <p className="text-zinc-400 max-w-xl mx-auto text-lg">
                Transforming raw African audio/video into "Gold-Standard" datasets via AI-Drafting and Human-Healing.
              </p>
            </div>
            <IngestionEngine onJobComplete={handleIngestionComplete} />
          </div>
        )}

        {view === ViewState.HEALER && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {currentJob ? (
               <HealerInterface 
                data={currentJob.syncMap} 
                onComplete={() => setView(ViewState.VAULT)}
                onTransaction={handleTransaction}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-[50vh] text-zinc-500">
                <LayoutGrid className="w-12 h-12 mb-4 opacity-20" />
                <p>No active job. Please ingest data first.</p>
                <button onClick={() => setView(ViewState.INGESTION)} className="mt-4 text-purple-400 hover:underline flex items-center gap-2">
                  Go to Ingestion <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {view === ViewState.VAULT && (
          <div className="animate-in zoom-in-95 duration-500">
            <VaultInterface />
          </div>
        )}

        {view === ViewState.ADMIN && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {user?.role === 'admin' ? (
              <AdminPanel />
            ) : (
              <div className="flex flex-col items-center justify-center h-[50vh]">
                <SetupAdmin onComplete={() => window.location.reload()} />
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-zinc-900 py-6 text-center text-xs text-zinc-600 font-mono">
        <p>IZWI PROJECT :: POWERED BY CLOUDFLARE WORKERS AI :: BANTU AXIOMS ENFORCED</p>
      </footer>
    </div>
  );
};

const NavButton = ({ label, active, onClick }: { label: string, active: boolean, onClick: () => void }) => (
  <button 
    onClick={onClick}
    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${active ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
  >
    {label}
  </button>
);

// Wrapper
export default function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}